import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { HandlingUnit } from '@klareospace/pharos-lib-types';
import type { DataTableColumn } from '@klareospace/pharos-lib-web-utils';
import { CodeCell, DataTable, MeasurementCell } from '@klareospace/pharos-lib-web-utils';

import { useHandlingUnitTypeLabels } from '../hooks/use-handling-unit-type-labels';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HandlingUnitsTableProps {
  readonly handlingUnits: HandlingUnit[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function HandlingUnitsTable({ handlingUnits }: HandlingUnitsTableProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('orders');
  const resolveTypeLabel = useHandlingUnitTypeLabels();

  const columns = useMemo<DataTableColumn<HandlingUnit>[]>(
    () => [
      {
        cell: (hu): React.ReactNode => <span>{resolveTypeLabel(hu.type)}</span>,
        header: t('drawer.content.columns.type'),
        key: 'type',
      },
      {
        cell: (hu): React.ReactNode => (
          <MeasurementCell
            className="text-foreground"
            unit={hu.weight ? tCommon(`units.${hu.weight.unit}`) : undefined}
            value={hu.weight?.value}
          />
        ),
        header: t('drawer.content.columns.weight'),
        key: 'weight',
      },
      {
        cell: (hu): React.ReactNode => (
          <MeasurementCell
            className="text-foreground"
            unit={hu.volume ? tCommon(`units.${hu.volume.unit}`) : undefined}
            value={hu.volume?.value}
          />
        ),
        header: t('drawer.content.columns.volume'),
        key: 'volume',
      },
      {
        cell: (hu): React.ReactNode => <CodeCell className="text-sm text-foreground">{hu.code?.value ?? '—'}</CodeCell>,
        header: t('drawer.content.columns.ean'),
        key: 'ean',
      },
      {
        cell: (hu): React.ReactNode => <span className="font-medium">{hu.quantity}</span>,
        cellClassName: 'text-end',
        className: 'text-end',
        header: t('drawer.content.columns.qty'),
        key: 'qty',
      },
    ],
    [resolveTypeLabel, t, tCommon],
  );

  return (
    <DataTable<HandlingUnit>
      ariaLabel={t('drawer.content.handlingUnitsAriaLabel')}
      columns={columns}
      emptyMessage={t('drawer.content.noHandlingUnits')}
      rows={handlingUnits}
    />
  );
}
